import {
  getParking,
  getRide,
  getRoad,
  getShares,
  getWalk,
  type CityWay,
  type Parking,
  type Point,
  type ShareStation,
  type ShareWay,
} from '@trippath/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModeIcon } from '../../components/icons/ModeIcon';
import { minutesSaid } from '../../lib/clock';
import { TRANSIT } from './dayWays';

/** How long one is followed by “Guide me” at most. */
const GUIDED = 30 * 60_000;

/** A timetable's mode as a town's own way: `subway` the metro, `rail` a train. */
export function cityWayOf(mode: string): CityWay {
  if (mode === 'tram' || mode === 'bus' || mode === 'ferry') return mode;
  if (mode === 'coach') return 'bus';
  if (/cable|funicular|lift/.test(mode)) return 'cable';
  if (/rail|long_distance/.test(mode)) return 'train';
  return 'metro';
}

/** Each way's mark in the map's colour of how it goes. */
const MARKS: Partial<Record<CityWay, string>> = {
  walk: 'var(--way-walk)',
  bike: 'var(--way-bike)',
  scooter: 'var(--way-cable)',
  metro: 'var(--way-metro)',
  tram: 'var(--way-tram)',
  train: 'var(--way-train)',
  cable: 'var(--way-cable)',
  bus: 'var(--way-bus)',
  ferry: 'var(--way-ferry)',
  taxi: 'var(--way-taxi)',
};

/** One way there, as offered: by what, its name and what is said under it, how long, its price. */
interface Choice {
  key: string;
  by: CityWay;
  name: string;
  how: string;
  /** What is said of now, set apart: `6 bikes free`. */
  now: string | null;
  minutes: number;
  price: string | null;
}

interface Props {
  from: Point;
  to: Point;
  /** Where it sets out from, by name: distances are from there. */
  fromName: string;
  /** When it sets out, an instant: the timetables are asked for then. */
  leaves: string;
  /** The way is today: what stations and vehicles have now is said, and read again each minute. */
  live: boolean;
  town: { country: string; city: string };
  /** How it goes as said; none, walked. */
  said: CityWay | null;
  /** The stop was come to by car: car parks near it to ride on from. */
  parkNear: Point | null;
  /** Told a way chosen; without it, on a trip read only, each is only said. */
  onPick?: ((by: CityWay, minutes: number) => void) | undefined;
  /** The stations near, and where one is, for the map. */
  onStations: (stations: ShareStation[]) => void;
  onMe: (me: Point | null) => void;
}

/**
 * The ways across town from one place of a day to the next (the `CityRides` mockup): on foot,
 * by the town's transport, by a shared bike or scooter, by taxi, each with how long and what it
 * costs, one pressed to take it; the stations near and what they have now; where one is, asked
 * only when pressed; car parks to ride on from when the town is come to by car.
 */
export function RideChoices({
  from,
  to,
  fromName,
  leaves,
  live,
  town,
  said,
  parkNear,
  onPick,
  onStations,
  onMe,
}: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [walk, setWalk] = useState<number | null>(null);
  const [transit, setTransit] = useState<{
    by: CityWay;
    line: string | null;
    minutes: number;
  }>();
  const [taxi, setTaxi] = useState<number | null>(null);
  const [shares, setShares] = useState<ShareWay[]>([]);
  // When they were read: how long ago a station said what it has is counted from then.
  const [readAt, setReadAt] = useState(0);
  const [parking, setParking] = useState<Parking[] | null>(null);
  const [guiding, setGuiding] = useState(false);
  const [lost, setLost] = useState(false);
  const [asked, setAsked] = useState(0);
  const ends = `${String(from.lat)},${String(from.lon)} ${String(to.lat)},${String(to.lon)}`;
  // The latest of what is asked and told, so a way is asked again only when its ends, its hour
  // or its day change, not because a handler or a point is a new one.
  const told = useRef({ onStations, onMe });
  const latest = useRef({ from, to, leaves, town, live, parkNear });
  useEffect(() => {
    told.current = { onStations, onMe };
    latest.current = { from, to, leaves, town, live, parkNear };
  });
  const parkAt = parkNear === null ? null : `${String(parkNear.lat)},${String(parkNear.lon)}`;

  useEffect(() => {
    const { from, to, leaves } = latest.current;
    let open = true;
    const settle =
      <T,>(set: (value: T) => void) =>
      (value: T) => {
        if (open) set(value);
      };
    getWalk(from, to).then(
      settle((road) => {
        setWalk(road?.minutes ?? null);
      }),
      () => undefined,
    );
    getRoad(from, to).then(
      // A taxi in town's traffic: about a quarter longer than the road's own time.
      settle((road) => {
        setTaxi(road ? Math.round(road.minutes * 1.25) : null);
      }),
      () => undefined,
    );
    getRide(from, to, leaves).then(
      settle((ride) => {
        const first = ride?.legs.find((leg) => leg.mode !== 'walk');
        setTransit(
          ride && first
            ? { by: cityWayOf(first.mode), line: first.line, minutes: ride.minutes }
            : undefined,
        );
      }),
      () => undefined,
    );
    return () => {
      open = false;
    };
  }, [ends, leaves]);

  useEffect(() => {
    const { from, to, town, live } = latest.current;
    let open = true;
    getShares(from, to, town, live).then(
      (read) => {
        if (!open) return;
        setShares(read.ways);
        setReadAt(Date.now());
        const docked = read.ways.find((way) => way.docked);
        told.current.onStations(
          docked ? [...docked.stations, ...(docked.to ? [docked.to] : [])] : [],
        );
      },
      () => undefined,
    );
    return () => {
      open = false;
    };
  }, [ends, live, town.country, town.city, asked]);

  // Today: what stations have now read again each minute while the page is seen.
  useEffect(() => {
    if (!live) return;
    const again = setInterval(() => {
      if (document.visibilityState === 'visible') setAsked((n) => n + 1);
    }, 60_000);
    return () => {
      clearInterval(again);
    };
  }, [live]);

  useEffect(() => {
    const { parkNear } = latest.current;
    if (parkNear === null) return;
    let open = true;
    getParking(parkNear).then(
      (read) => {
        if (open) setParking(read);
      },
      () => undefined,
    );
    return () => {
      open = false;
    };
  }, [parkAt]);

  // Nothing of this way on the map once it is left.
  useEffect(
    () => () => {
      told.current.onStations([]);
      told.current.onMe(null);
    },
    [],
  );

  // Followed only while guiding, and at most half an hour, and never while the page is hidden.
  useEffect(() => {
    if (!guiding) return;
    const watch = navigator.geolocation.watchPosition(
      (at) => {
        told.current.onMe({ lat: at.coords.latitude, lon: at.coords.longitude });
      },
      () => {
        setLost(true);
        setGuiding(false);
      },
    );
    const stop = () => {
      setGuiding(false);
    };
    const enough = setTimeout(stop, GUIDED);
    const hidden = () => {
      if (document.visibilityState === 'hidden') stop();
    };
    document.addEventListener('visibilitychange', hidden);
    return () => {
      navigator.geolocation.clearWatch(watch);
      clearTimeout(enough);
      document.removeEventListener('visibilitychange', hidden);
    };
  }, [guiding]);

  const money = (amount: number, currency: string) =>
    new Intl.NumberFormat(lang, {
      style: 'currency',
      currency,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  const metres = (m: number) =>
    m < 1000 ? `${String(Math.round(m))} m` : `${(m / 1000).toLocaleString(lang)} km`;
  const ago = (at: number) =>
    new Intl.RelativeTimeFormat(lang, { numeric: 'auto' }).format(
      -Math.max(0, Math.round((readAt - at * 1000) / 60_000)),
      'minute',
    );

  const choices: Choice[] = [
    ...(walk === null
      ? []
      : [
          {
            key: 'walk',
            by: 'walk' as const,
            name: t('rides.walk'),
            how: t('rides.walkHow'),
            now: null,
            minutes: walk,
            price: null,
          },
        ]),
    ...(transit
      ? [
          {
            key: 'transit',
            by: transit.by,
            name:
              transit.line === null
                ? t('rides.transit')
                : t('rides.transitLine', {
                    mode: t(`city.by.${transit.by}`).replace(/^./, (c) => c.toUpperCase()),
                    line: transit.line,
                  }),
            how: t('rides.transitHow'),
            now: null,
            minutes: transit.minutes,
            price: null,
          },
        ]
      : []),
    ...shares.map((way, at) => ({
      key: `share ${String(at)}`,
      by: way.kind,
      name: t(way.kind === 'bike' ? 'rides.bike' : 'rides.scooter', { system: way.system }),
      how: way.from
        ? t('rides.station', { metres: metres(way.from.metres) })
        : t('rides.anywhere'),
      now: way.from
        ? way.from.bikes === null
          ? null
          : `${t('rides.free', { count: way.from.bikes })}${
              way.from.at === null ? '' : `, ${ago(way.from.at)}`
            }`
        : way.near === null
          ? null
          : t('rides.near', { count: way.near }),
      minutes: way.minutes,
      price: way.price ? money(way.price.amount, way.price.currency) : null,
    })),
    ...(taxi === null
      ? []
      : [
          {
            key: 'taxi',
            by: 'taxi' as const,
            name: t('rides.taxi'),
            how: t('rides.taxiHow'),
            now: null,
            minutes: taxi,
            price: null,
          },
        ]),
  ];
  // The way as said, pressed: the first offered that goes by it; a way said by none, walked.
  const wanted = said ?? 'walk';
  const chosen = choices.find((choice) =>
    TRANSIT.has(wanted) ? TRANSIT.has(choice.by) : choice.by === wanted,
  )?.key;
  const docked = shares.find((way) => way.docked);

  const showMe = () => {
    setLost(false);
    navigator.geolocation.getCurrentPosition(
      (at) => {
        onMe({ lat: at.coords.latitude, lon: at.coords.longitude });
      },
      () => {
        setLost(true);
      },
    );
  };

  return (
    <div className="rides">
      <div className="rides-choices" role="group" aria-label={t('rides.ways')}>
        {choices.length === 0 && <p className="quiet">{t('rides.loading')}</p>}
        {choices.map((choice) => (
          <button
            key={choice.key}
            type="button"
            className="rides-choice"
            aria-pressed={choice.key === chosen}
            disabled={onPick === undefined}
            onClick={() => {
              onPick?.(choice.by, choice.minutes);
            }}
          >
            <span className="rides-mark" style={{ background: MARKS[choice.by] }}>
              <ModeIcon mode={choice.by} size={14} />
            </span>
            <span className="rides-name">
              <b>{choice.name}</b>
              <small>
                {choice.how}
                {choice.now !== null && (
                  <>
                    {' · '}
                    <span className="rides-live">{choice.now}</span>
                  </>
                )}
              </small>
            </span>
            <span className="rides-time">
              {minutesSaid(choice.minutes)}
              {choice.price !== null && <small>{choice.price}</small>}
            </span>
          </button>
        ))}
      </div>
      {docked && (
        <section className="rides-section" aria-label={t('rides.stations')}>
          <h3 className="caps">{t('rides.stations')}</h3>
          <ul className="rides-stations">
            {docked.stations.map((station) => (
              <li key={`${station.name} ${String(station.lat)}`}>
                <span>{station.name}</span>
                <span className="mono">{metres(station.metres)}</span>
                {station.bikes !== null && (
                  <span
                    className={`tag ${station.bikes > 0 ? 'rides-tag-ok' : 'rides-tag-warn'}`}
                  >
                    {t('rides.bikes', { count: station.bikes })}
                  </span>
                )}
              </li>
            ))}
            {docked.to && (
              <li>
                <span>{docked.to.name}</span>
                <span className="mono">{t('rides.atEnd')}</span>
                {docked.to.docks !== null && (
                  <span
                    className={`tag ${docked.to.docks > 0 ? 'rides-tag-ok' : 'rides-tag-warn'}`}
                  >
                    {t('rides.docks', { count: docked.to.docks })}
                  </span>
                )}
              </li>
            )}
          </ul>
          <p className="quiet rides-note">{t('rides.stationsNote')}</p>
        </section>
      )}
      <section className="rides-section" aria-label={t('rides.location')}>
        <h3 className="caps">{t('rides.location')}</h3>
        <p className="rides-note">{t('rides.locationFrom', { place: fromName })}</p>
        <p className="rides-note">{t('rides.locationOnce')}</p>
        <p className="quiet rides-note">{t('rides.locationNever')}</p>
        <div className="rides-buttons">
          <button type="button" className="button button-small" onClick={showMe}>
            {t('rides.showMe')}
          </button>
          <button
            type="button"
            className="button button-small"
            aria-pressed={guiding}
            onClick={() => {
              setLost(false);
              setGuiding(!guiding);
            }}
          >
            {t(guiding ? 'rides.stopGuide' : 'rides.guide')}
          </button>
        </div>
        {lost && (
          <p className="form-failed" role="alert">
            {t('rides.noLocation')}
          </p>
        )}
      </section>
      {parkNear !== null && parking !== null && (
        <section className="rides-section" aria-label={t('rides.parking')}>
          <h3 className="caps">{t('rides.parking')}</h3>
          {parking.length === 0 ? (
            <p className="quiet rides-note">{t('rides.parkingNone')}</p>
          ) : (
            <ul className="rides-stations">
              {parking.map((park) => (
                <li key={`${String(park.lat)} ${String(park.lon)}`}>
                  <span>
                    {park.name === '' ? 'P+R' : park.name}
                    <small className="quiet">
                      {t(
                        `rides.parkingBy.${park.by === 'train' || park.by === 'bus' ? park.by : 'other'}`,
                      )}{' '}
                      · {metres(park.metres)}
                    </small>
                  </span>
                  <span className="mono">
                    {park.charge ?? (park.fee === false ? t('rides.parkingFree') : '')}
                  </span>
                  <span className="tag">OSM</span>
                </li>
              ))}
            </ul>
          )}
          <p className="quiet rides-note">{t('rides.parkingNote')}</p>
        </section>
      )}
    </div>
  );
}
